import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import PaginaSuscripcion from '../components/suscripcion/PaginaSuscripcion';
import TarjetaAU from '../components/suscripcion/TarjetaAU';
import EtiquetaAU from '../components/suscripcion/EtiquetaAU';
import BotonAU from '../components/suscripcion/BotonAU';
import CirculoIcono from '../components/suscripcion/CirculoIcono';
import ErrorConCodigo from '../components/ErrorConCodigo';
import { formatFechaConOffset } from '../utils/fechaSuscripcion';
import { enlaceWhatsApp } from '../utils/whatsapp';
import { AU, FUENTE, SOMBRA_TARJETA } from '../styles/tokensAU';

const Titulo = ({ children }) => (
  <Box component="h1" sx={{ m: 0, fontFamily: FUENTE, fontSize: 26, fontWeight: 800, lineHeight: 1.15, color: AU.oscuro }}>
    {children}
  </Box>
);

const Parrafo = ({ children }) => (
  <Box sx={{ fontFamily: FUENTE, fontSize: 15, color: AU.grisMedio }}>{children}</Box>
);

// A esta página llega la persona desde Flow (vía /api/subscription/enrollment-return) tras
// registrar su tarjeta. Confirma el registro y crea la suscripción. Recargarla es seguro: el
// backend no crea una segunda suscripción si ya existe. El backend crea solo con lo que guardó al
// abrir el registro de tarjeta; el plan y el inicio de la URL le sirven solo para reconocer una
// suscripción ya creada.
const SubscriptionConfirmPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [estado, setEstado] = useState('cargando'); // 'cargando' | 'listo' | 'error'
  const [error, setError] = useState({ mensaje: '', codigo: '' });
  const [proximoCobro, setProximoCobro] = useState(null);
  const yaConfirmo = useRef(false);

  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  const planId = params.get('planId');
  const start = params.get('start');

  useEffect(() => {
    if (yaConfirmo.current) return;
    yaConfirmo.current = true;

    if (!token) {
      setError({
        mensaje: 'Falta el código de Flow. Si llegaste a esta página sin terminar el registro de tu tarjeta, vuelve a intentarlo.',
        codigo: '',
      });
      setEstado('error');
      return;
    }
    if (!planId) {
      setError({
        mensaje: 'Falta el plan, así que no podemos crear la suscripción. Vuelve a abrir el link de suscripción que te enviamos.',
        codigo: '',
      });
      setEstado('error');
      return;
    }

    axios.post(`${process.env.REACT_APP_API_URL}/api/subscription/complete-enrollment`, {
      token,
      planId,
      subscription_start: start,
    })
      .then(({ data }) => {
        setProximoCobro(data?.proximoCobro || null);
        setEstado('listo');
      })
      .catch((err) => {
        const data = err.response?.data;
        setError({
          mensaje: data?.message
            || (err.response
              ? 'No pudimos confirmar el registro de tu tarjeta.'
              : 'No pudimos conectar con el servidor. Revisa tu conexión e intenta de nuevo.'),
          codigo: data?.code || '',
        });
        setEstado('error');
      });
  }, [token, planId, start]);

  // Volver al formulario con el mismo plan y la misma fecha: sin esto caería al plan por
  // defecto del .env, que puede ser otro. Los datos personales no viajan por la URL: se reescriben.
  const reintentar = () => {
    const q = new URLSearchParams();
    if (planId) q.set('plan_id', planId);
    if (start) q.set('start', start);
    navigate(q.toString() ? `/subscribe?${q.toString()}` : '/subscribe');
  };

  const fechaCobro = formatFechaConOffset(proximoCobro);

  return (
    <PaginaSuscripcion>
      {estado === 'cargando' && (
        <Box role="status" sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: AU.azul }} aria-hidden="true" />
          <Titulo>Confirmando tu suscripción...</Titulo>
          <Parrafo>Estamos validando el registro de tu tarjeta con Flow.</Parrafo>
        </Box>
      )}

      {estado === 'listo' && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center', pt: 2 }}>
            <CirculoIcono><CheckRoundedIcon /></CirculoIcono>
            <Titulo>¡Listo! Tu tarjeta quedó registrada</Titulo>
          </Box>
          {fechaCobro && (
            <TarjetaAU sx={{ textAlign: 'center', bgcolor: AU.azulHover, boxShadow: SOMBRA_TARJETA }}>
              <EtiquetaAU sx={{ color: AU.azulSuave }}>Próximo cobro</EtiquetaAU>
              <Box sx={{ mt: 0.5, fontFamily: FUENTE, fontSize: 28, fontWeight: 800, color: AU.blanco }}>{fechaCobro}</Box>
            </TarjetaAU>
          )}
          <Box sx={{ textAlign: 'center' }}>
            <Parrafo>Si algo no calza, escríbenos por WhatsApp.</Parrafo>
          </Box>
          <BotonAU onClick={() => navigate('/welcome')}>Continuar</BotonAU>
          <BotonAU component="a" href={enlaceWhatsApp()} target="_blank" rel="noopener noreferrer" variante="secundario">
            Escríbenos por WhatsApp
          </BotonAU>
        </>
      )}

      {estado === 'error' && (
        <>
          <Titulo>No pudimos confirmar tu suscripción</Titulo>
          <ErrorConCodigo mensaje={error.mensaje} codigo={error.codigo} />
          <BotonAU onClick={reintentar}>Volver a intentar</BotonAU>
        </>
      )}
    </PaginaSuscripcion>
  );
};

export default SubscriptionConfirmPage;
